// genericFallbacks.ts
// Segmentações genéricas de fallback — usadas quando o OpenRouter falha após N tentativas.
// Cada entrada serve bem para qualquer tipo de negócio brasileiro.

export interface FallbackSegmentation {
  id: number;
  label: string; // Nome amigável para logs
  content: string;
}

export const GENERIC_FALLBACKS: FallbackSegmentation[] = [
  // ─────────────────────────────────────────────
  // FALLBACK 1 — Perfil Consumidor Digital Ativo
  // ─────────────────────────────────────────────
  {
    id: 1,
    label: 'Consumidor Digital Ativo',
    content: `1. Persona Ideal
Nome fictício e idade: Lucas Ferreira, 32 anos
Gênero: Masculino (51%) / Feminino (49%)
Profissão: Profissional liberal, autônomo ou CLT de nível médio/sênior
Renda média: R$ 3.500 – R$ 7.000 / mês
Comportamentos: Pesquisa muito antes de comprar, consome conteúdo no Instagram e YouTube, compara preços online, valoriza avaliações de outros clientes.
Dores e desejos: Quer praticidade, boa relação custo-benefício e atendimento rápido. Tem medo de ser enganado e preza por transparência.

2. Palavras-chave para Google Ads
Termos de pesquisa principais:
- melhor [produto/serviço] em [cidade]
- [produto/serviço] preço
- contratar [serviço] online
- [serviço] perto de mim
- como funciona [serviço]
Palavras-chave de cauda longa:
- quanto custa [serviço] em [cidade]
- [serviço] para pequenas empresas
- [produto] entrega rápida
- avaliação de [serviço/produto]
- melhor empresa de [serviço] no Brasil
- [serviço] confiável com garantia
- [produto] promoção hoje
- contratar [serviço] agora
- [serviço] para pessoa física
Termos negativos sugeridos:
- grátis, download, pirata, como fazer sozinho, tutorial gratuito

3. Interesses para Meta Ads
Interesses principais (mínimo 10):
- Compras online
- Promoções e descontos
- Empreendedorismo
- Tecnologia e gadgets
- Finanças pessoais
- Lifestyle e bem-estar
- Família e lar
- Viagens e turismo
- Alimentação saudável
- Moda e estilo
Comportamentos de compra: Compradores frequentes online, usuários de e-commerce, engajados com anúncios.
Hobbies e atividades: Academia, streaming, culinária, leitura digital.
Sugestão para Bio do Instagram: "Soluções práticas para o seu dia a dia | Atendimento rápido | Peça agora pelo link 👇"
Hashtags para Posts: #qualidade #promoção #oferta #compraonline #entregaexpressa #satisfaçãogarantida #brasil #pequenaempresa

4. Dados Demográficos
Faixa etária detalhada: 25–45 anos (foco em 28–38)
Localização geográfica: Brasil inteiro, priorizando capitais e cidades médias (pop. acima de 100 mil)
Nível educacional: Ensino médio completo a superior
Estado civil: Solteiros e casados, com ou sem filhos
Poder aquisitivo: Classes B e C (renda familiar R$ 3.000–R$ 10.000)

5. Funis e Ofertas
Jornada do cliente:
- Descoberta → Consideração → Decisão → Fidelização
Pontos de contato:
- Instagram Ads, Google Search, WhatsApp, e-mail marketing
Ofertas para cada etapa:
- Topo: conteúdo educativo, post de valor, reels informativos
- Meio: depoimento de clientes, comparativo, demonstração
- Fundo: desconto exclusivo, frete grátis, garantia estendida
Gatilhos de conversão:
- Escassez ("Últimas vagas/unidades")
- Prova social (número de clientes atendidos)
- Garantia de satisfação (devolução em X dias)
- Bônus surpresa

6. Dicas de SEO
Termos principais: [nome do serviço/produto] + cidade, avaliação + [serviço], melhor + [produto]
Estrutura de conteúdo:
- Blog com posts de dúvidas frequentes
- FAQ na página principal
- Página "Sobre" com história e valores
Otimizações técnicas:
- Site mobile-first com carregamento < 3s
- Google Meu Negócio atualizado com fotos e horários
- Schema Markup para produtos e avaliações
Estratégia de backlinks:
- Parceria com blogs locais e influencers de nicho
- Cadastro em diretórios (Reclame Aqui, Yelp, Guia local)

7. Recomendações de Investimento
Orçamento inicial sugerido: R$ 800 – R$ 2.000/mês
Distribuição entre plataformas:
- Google Ads (Search): 45%
- Meta Ads (Instagram + Facebook): 40%
- Remarketing: 15%
Períodos de teste: 30 dias para aprendizado, ajuste de criativos na semana 3
ROI esperado: 200%–400% após 60–90 dias de otimização contínua`,
  },

  // ─────────────────────────────────────────────
  // FALLBACK 2 — Perfil Mulher Decisora de Compra
  // ─────────────────────────────────────────────
  {
    id: 2,
    label: 'Mulher Decisora de Compra',
    content: `1. Persona Ideal
Nome fictício e idade: Camila Souza, 35 anos
Gênero: Feminino (predominante)
Profissão: Professora, enfermeira, gestora, empreendedora ou dona de casa com renda própria
Renda média: R$ 2.800 – R$ 6.500 / mês
Comportamentos: Pesquisa no Google e Pinterest antes de decidir, compartilha achados com amigas, lê avaliações no Instagram, faz pesquisa de preços no WhatsApp.
Dores e desejos: Quer se sentir valorizada, busca produtos/serviços que facilitem a rotina. Prioriza confiança, recomendação de amigas e bom atendimento.

2. Palavras-chave para Google Ads
Termos de pesquisa principais:
- [produto/serviço] feminino
- melhor [serviço] para mulheres
- [produto] indicado
- onde comprar [produto] barato
- [serviço] confiável
Palavras-chave de cauda longa:
- [produto] qualidade boa e barato
- [serviço] para mães
- [produto] funciona mesmo?
- melhor custo benefício [produto/serviço]
- [serviço] entrega em casa
- promoção [produto] hoje
- [serviço] para mulher ocupada
- como escolher [produto/serviço]
- [produto] vale a pena?
Termos negativos sugeridos:
- masculino, importado sem garantia, fake, golpe

3. Interesses para Meta Ads
Interesses principais (mínimo 10):
- Beleza e cuidados pessoais
- Moda e tendências
- Maternidade e família
- Saúde e bem-estar
- Culinária e receitas
- Decoração e casa
- Finanças femininas
- Educação dos filhos
- Empreendedorismo feminino
- Yoga e meditação
Comportamentos de compra: Compradoras frequentes online, engajadas com influencers, usuárias de apps de compras.
Hobbies e atividades: Academia, Netflix, leitura, viagens em família, artesanato.
Sugestão para Bio do Instagram: "Feito para mulheres que merecem o melhor ✨ | Entrega em todo o Brasil | Fale comigo no link 👇"
Hashtags para Posts: #mulheresqueinspiram #empoderamento #vidaequilibrada #dicasfemininas #autoestima #rotina #cuidadospessoais #brasil

4. Dados Demográficos
Faixa etária detalhada: 24–44 anos (foco em 27–40)
Localização geográfica: Todo o Brasil, foco em regiões Sudeste e Sul
Nível educacional: Ensino médio completo a pós-graduação
Estado civil: Casadas ou em relacionamentos (55%), solteiras (45%)
Poder aquisitivo: Classes B e C

5. Funis e Ofertas
Jornada do cliente:
- Inspiração → Pesquisa → Comparação → Compra → Indicação
Pontos de contato:
- Reels no Instagram, Pinterest, WhatsApp, e-mail
Ofertas para cada etapa:
- Topo: conteúdo inspirador, depoimentos emocionais, antes e depois
- Meio: comparativo de benefícios, tutorial, live de demonstração
- Fundo: oferta por tempo limitado, frete grátis, brinde exclusivo
Gatilhos de conversão:
- Comunidade ("Mais de X mulheres já aprovaram")
- Exclusividade ("Edição limitada")
- Facilidade de pagamento (parcelamento sem juros)
- Depoimento de cliente real

6. Dicas de SEO
Termos principais: [produto/serviço] feminino + cidade, melhor [produto] para mulheres
Estrutura de conteúdo:
- Blog com dicas práticas do cotidiano
- Depoimentos em vídeo
- Sessão de perguntas frequentes
Otimizações técnicas:
- Página responsiva com imagens otimizadas
- Integração com WhatsApp Business
- Cadastro no Google Meu Negócio
Estratégia de backlinks:
- Parcerias com influencers e micro-influencers
- Grupos femininos no Facebook e Telegram

7. Recomendações de Investimento
Orçamento inicial sugerido: R$ 700 – R$ 1.800/mês
Distribuição entre plataformas:
- Meta Ads (Instagram + Facebook): 55%
- Google Ads: 30%
- WhatsApp Marketing: 15%
Períodos de teste: 30 dias para identificar criativo vencedor
ROI esperado: 250%–450% com criativos bem segmentados após 45–60 dias`,
  },

  // ────────────────────────────────────────────────
  // FALLBACK 3 — Perfil Empreendedor / Empresa B2B
  // ────────────────────────────────────────────────
  {
    id: 3,
    label: 'Empreendedor e Empresas B2B',
    content: `1. Persona Ideal
Nome fictício e idade: Roberto Almeida, 40 anos
Gênero: Masculino (60%) / Feminino (40%)
Profissão: Empresário, gerente, diretor ou autônomo com CNPJ
Renda média: R$ 6.000 – R$ 20.000+ / mês
Comportamentos: Toma decisões baseado em ROI, pesquisa no LinkedIn e Google, valoriza cases de sucesso e dados concretos. Prefere fornecedores com suporte eficiente.
Dores e desejos: Reduzir custos, aumentar eficiência, escalar o negócio, não perder tempo com soluções que não funcionam.

2. Palavras-chave para Google Ads
Termos de pesquisa principais:
- [serviço] para empresas
- fornecedor de [produto/serviço] B2B
- [solução] corporativa
- contratar [serviço] para CNPJ
- [produto] atacado
Palavras-chave de cauda longa:
- melhor [serviço] para pequenas empresas
- [produto/serviço] com nota fiscal
- [solução] para escalar negócio
- custo de [serviço] para empresa
- [serviço] com contrato e suporte
- [produto] para revenda
- como reduzir custo com [serviço]
- [solução] para gestão empresarial
- [serviço] para PME
Termos negativos sugeridos:
- pessoal, grátis, estudante, sem suporte

3. Interesses para Meta Ads
Interesses principais (mínimo 10):
- Empreendedorismo
- Gestão de negócios
- Marketing digital
- Vendas e CRM
- Finanças empresariais
- Automação e tecnologia
- Recursos humanos
- E-commerce
- Franquias e negócios
- Produtividade e gestão do tempo
Comportamentos de compra: Tomadores de decisão, administradores de páginas de negócio, usuários de ferramentas de negócios.
Hobbies e atividades: Podcasts de negócios, eventos e feiras, LinkedIn, cursos de gestão.
Sugestão para Bio do Instagram: "Soluções para empresas que querem crescer | Cases comprovados | Fale com especialista 👇"
Hashtags para Posts: #empreendedorismo #pme #negócios #gestão #crescimentoempresarial #marketingdigital #vendas #b2b #brasil

4. Dados Demográficos
Faixa etária detalhada: 28–52 anos (foco em 32–48)
Localização geográfica: Todo o Brasil, foco em capitais e polos industriais
Nível educacional: Superior completo, MBA e pós-graduação
Estado civil: Casados (65%), solteiros (35%)
Poder aquisitivo: Classes A e B

5. Funis e Ofertas
Jornada do cliente:
- Consciência do problema → Avaliação de soluções → Proposta → Fechamento → Retenção
Pontos de contato:
- LinkedIn Ads, Google Search, e-mail marketing, webinars
Ofertas para cada etapa:
- Topo: e-book gratuito, artigo de blog, vídeo educativo
- Meio: estudo de caso, demonstração gratuita, consultoria inicial
- Fundo: proposta personalizada, teste grátis por 14 dias, desconto para contrato anual
Gatilhos de conversão:
- Dados e métricas de resultado
- Depoimentos de empresas similares
- Garantia contratual
- Urgência (vagas limitadas para onboarding)

6. Dicas de SEO
Termos principais: [serviço] para empresas + setor, fornecedor [produto] + estado
Estrutura de conteúdo:
- Cases de sucesso detalhados no site
- Blog com artigos técnicos e tendências do setor
- Página de comparativo vs concorrentes
Otimizações técnicas:
- Site rápido com SSL e design profissional
- Integração com CRM e formulários de captação
- Schema Markup para reviews e produtos
Estratégia de backlinks:
- Guest posts em portais de negócios (Sebrae, Exame, Forbes BR)
- Parcerias com associações comerciais e sindicatos

7. Recomendações de Investimento
Orçamento inicial sugerido: R$ 1.500 – R$ 5.000/mês
Distribuição entre plataformas:
- Google Ads (Search + Display): 50%
- LinkedIn Ads: 30%
- Remarketing e e-mail: 20%
Períodos de teste: 45 dias para ciclo de vendas B2B
ROI esperado: 300%–600% com foco em LTV (valor do cliente no longo prazo)`,
  },

  // ────────────────────────────────────────────────
  // FALLBACK 4 — Perfil Jovem Urbano Conectado
  // ────────────────────────────────────────────────
  {
    id: 4,
    label: 'Jovem Urbano Conectado',
    content: `1. Persona Ideal
Nome fictício e idade: Beatriz Lima, 24 anos
Gênero: Masculino (48%) / Feminino (52%)
Profissão: Estudante universitário, profissional júnior, freelancer ou influencer iniciante
Renda média: R$ 1.500 – R$ 4.000 / mês
Comportamentos: Vive no celular, consome TikTok e Reels, decide por impulso com base em reviews, compartilha compras nas stories, prefere pagar por PIX ou cartão.
Dores e desejos: Quer experiências únicas, produtos com identidade, marcas com propósito. Sente pressão por pertencimento e quer mostrar estilo.

2. Palavras-chave para Google Ads
Termos de pesquisa principais:
- [produto/serviço] viral
- melhor [produto] custo benefício
- [produto] tendência
- [serviço] online rápido
- comprar [produto] pelo celular
Palavras-chave de cauda longa:
- [produto] que todo mundo está usando
- [serviço] para jovens
- [produto] entrega no mesmo dia
- melhor app de [serviço]
- [produto] com desconto no pix
- [serviço] sem mensalidade
- [produto] novo lançamento
- onde tem [produto] mais barato
- [serviço] fácil de usar
Termos negativos sugeridos:
- para idosos, caro demais, complicado, lento

3. Interesses para Meta Ads
Interesses principais (mínimo 10):
- Música e entretenimento
- Moda streetwear e tendências
- Games e esports
- Redes sociais e criação de conteúdo
- Gastronomia e food
- Viagens de baixo custo
- Cultura pop e anime
- Tecnologia e smartphones
- Fitness e treinos
- Sustentabilidade e causas sociais
Comportamentos de compra: Compradores mobile, usuários de apps de entrega, seguidores de influencers, engajados com sorteios.
Hobbies e atividades: TikTok, shows, cafés especiais, esportes urbanos, cursos online.
Sugestão para Bio do Instagram: "O que você precisava encontrar 🔥 | Entrega rápida | Pague com PIX | Link na bio 👇"
Hashtags para Posts: #viral #tendência #novidade #pix #entrega #lifestyle #jovem #brasil #swipe

4. Dados Demográficos
Faixa etária detalhada: 18–30 anos (foco em 19–26)
Localização geográfica: Capitais e cidades universitárias do Brasil
Nível educacional: Ensino médio a superior em andamento
Estado civil: Solteiros (75%), em relacionamentos (25%)
Poder aquisitivo: Classes C e B

5. Funis e Ofertas
Jornada do cliente:
- Descoberta via feed → Engajamento → Impulsividade → Compra → Compartilhamento
Pontos de contato:
- TikTok Ads, Instagram Reels, Stories, influencers
Ofertas para cada etapa:
- Topo: vídeo curto (<15s), meme de produto, conteúdo divertido
- Meio: tutorial rápido, unboxing, "comprei e amei"
- Fundo: desconto no PIX, oferta relâmpago, frete grátis no primeiro pedido
Gatilhos de conversão:
- Tempo limitado ("acaba em X horas")
- Prova social instantânea (curtidas e comentários)
- FOMO ("todo mundo está comprando")
- Facilidade extrema (1 clique para comprar)

6. Dicas de SEO
Termos principais: [produto] viral + ano, melhor [produto] para jovens
Estrutura de conteúdo:
- Vídeos curtos com SEO para YouTube Shorts e TikTok
- Blog com linguagem descontraída
- FAQ em formato stories
Otimizações técnicas:
- Checkout mobile-first em menos de 3 etapas
- Links diretos via Instagram Shopping
- Google Meu Negócio com fotos modernas
Estratégia de backlinks:
- Parcerias com micro-influencers (5k–100k seguidores)
- Mídias digitais jovens (Buzzfeed BR, Vice, portais universitários)

7. Recomendações de Investimento
Orçamento inicial sugerido: R$ 500 – R$ 1.500/mês
Distribuição entre plataformas:
- Meta Ads (Instagram + Facebook): 50%
- TikTok Ads: 30%
- Influencer marketing: 20%
Períodos de teste: 15–21 dias (ciclo de compra curto)
ROI esperado: 300%–500% com criativos virais e bom produto`,
  },

  // ──────────────────────────────────────────────────────
  // FALLBACK 5 — Perfil Família Brasileira / Classe Média
  // ──────────────────────────────────────────────────────
  {
    id: 5,
    label: 'Família Brasileira Classe Média',
    content: `1. Persona Ideal
Nome fictício e idade: Fernanda e Marcos Costa, 38 e 41 anos
Gênero: Feminino (decisão de compra: 65%) / Masculino (35%)
Profissão: Professores, servidores públicos, profissionais da saúde, comerciantes
Renda média: R$ 4.000 – R$ 9.000 / mês (renda familiar)
Comportamentos: Pesquisam bastante antes de comprar, priorizam segurança e qualidade, comparam preços no Google e Zoom, confiam em marcas conhecidas e indicações de amigos.
Dores e desejos: Querem o melhor para a família com o menor custo. Preocupam-se com segurança, durabilidade e suporte pós-venda. Valorizam parcelamento e facilidade de pagamento.

2. Palavras-chave para Google Ads
Termos de pesquisa principais:
- [produto/serviço] para família
- melhor [produto] para casa
- [serviço] confiável com garantia
- [produto] para crianças
- [serviço] perto de mim
Palavras-chave de cauda longa:
- [produto] bom e barato para casa
- [serviço] parcelado sem juros
- onde comprar [produto] com garantia
- [produto] indicado para família
- [serviço] atende fins de semana
- [produto] resistente e durável
- [serviço] com atendimento humanizado
- orçamento de [serviço] online
- [produto] entrega em casa com nota fiscal
Termos negativos sugeridos:
- importado sem suporte, genérico, sem garantia, arriscado

3. Interesses para Meta Ads
Interesses principais (mínimo 10):
- Família e parentalidade
- Educação dos filhos
- Casa e decoração
- Saúde e prevenção
- Culinária e receitas em família
- Viagens de férias
- Finanças e economia doméstica
- Pets e animais de estimação
- Segurança residencial
- Supermercado e compras do mês
Comportamentos de compra: Compradores planejados, usuários de comparadores de preço, fiéis a marcas de confiança.
Hobbies e atividades: Churrasco em família, parques, cinema, escola dos filhos, jardim.
Sugestão para Bio do Instagram: "Qualidade e segurança para sua família 🏠❤️ | Atendimento de segunda a sábado | Peça pelo link 👇"
Hashtags para Posts: #família #lar #qualidade #segurança #criançafeliz #casaorganizada #vidadefamília #compraconsciente #brasil

4. Dados Demográficos
Faixa etária detalhada: 30–50 anos (foco em 33–46)
Localização geográfica: Interior e capitais de todo o Brasil
Nível educacional: Ensino médio a superior completo
Estado civil: Casados ou em união estável (70%), com filhos (65%)
Poder aquisitivo: Classes B e C

5. Funis e Ofertas
Jornada do cliente:
- Necessidade identificada → Pesquisa → Avaliação → Decisão → Recompra
Pontos de contato:
- Google Search, Facebook, WhatsApp, indicações boca a boca
Ofertas para cada etapa:
- Topo: conteúdo educativo, artigos de blog, posts de dicas práticas
- Meio: comparativo de produtos, vídeo explicativo, depoimentos de família real
- Fundo: parcelamento especial, garantia estendida, brinde com compra
Gatilhos de conversão:
- Segurança e confiança ("Mais de X famílias atendidas")
- Facilidade de pagamento ("12x sem juros")
- Suporte garantido ("Atendimento 7 dias por semana")
- Avaliações positivas verificadas

6. Dicas de SEO
Termos principais: [produto/serviço] + cidade, [produto] para família, [serviço] bairro/região
Estrutura de conteúdo:
- Blog com dicas para o lar e família
- Perguntas frequentes bem detalhadas
- Galeria de fotos reais de clientes
Otimizações técnicas:
- Site com chat online e WhatsApp visível
- Google Meu Negócio com avaliações e respostas
- Velocidade mobile < 3 segundos
Estratégia de backlinks:
- Parcerias com escolas, associações de bairro, portais de notícias locais
- Cadastro no Reclame Aqui e manutenção da nota

7. Recomendações de Investimento
Orçamento inicial sugerido: R$ 900 – R$ 2.500/mês
Distribuição entre plataformas:
- Google Ads (Search + Maps): 45%
- Facebook Ads: 35%
- WhatsApp e e-mail marketing: 20%
Períodos de teste: 30–45 dias com foco em conversão
ROI esperado: 200%–380% com estratégia de relacionamento e fidelização`,
  },
];

/**
 * Retorna uma segmentação genérica aleatória, evitando repetir a última usada.
 * @param lastId - ID da última segmentação usada (para evitar repetição)
 */
export const getRandomFallback = (lastId?: number): FallbackSegmentation => {
  const available = lastId
    ? GENERIC_FALLBACKS.filter((f) => f.id !== lastId)
    : GENERIC_FALLBACKS;

  const randomIndex = Math.floor(Math.random() * available.length);
  return available[randomIndex];
};
